import { createServerFn } from '@tanstack/react-start'
import { z } from 'zod'
import { getMergedProductById } from '#bv/lib/productCatalog'
import { ingestMoneyMotionPendingCheckout } from '#bv/lib/adminOrderIngest'
import { createMoneyMotionCheckoutSession, moneyMotionCheckoutUrl } from '#bv/lib/moneymotionClient'
import { assertProductsInStockOrThrow } from '#bv/lib/productStock'
import { isSmmProduct, smmMetadataForCheckout } from '#bv/lib/smmProduct'
import { normalizeSmmTargetForProduct, smmTargetValidationMessageForProduct } from '#bv/lib/smmTarget'
import { smmTargetFromCartLine } from '#bv/lib/smmProduct'
import { parseBuyerEmailFromCheckoutPayload } from '#bv/lib/buyerEmail'

function getApiKey(): string | undefined {
  return process.env.MONEYMOTION_API_KEY?.trim() || process.env.MONEYMOTION_SECRET_KEY?.trim()
}

function defaultGuestEmail(): string {
  return (
    process.env.MONEYMOTION_CHECKOUT_EMAIL?.trim() ||
    process.env.CHECKOUT_GUEST_EMAIL?.trim() ||
    ''
  )
}

export const getMoneyMotionEnabled = createServerFn({ method: 'GET' }).handler(() => {
  return !!getApiKey()
})

const checkoutInputSchema = z.object({
  productId: z.number().int().positive(),
  email: z.string().optional(),
})

export const createCheckoutSession = createServerFn({
  method: 'POST',
})
  .inputValidator((data: unknown) => checkoutInputSchema.parse(data))
  .handler(async ({ data }) => {
    const { productId } = data
    const apiKey = getApiKey()
    if (!apiKey) {
      throw new Error('MoneyMotion is not configured (set MONEYMOTION_API_KEY).')
    }
    const product = await getMergedProductById(productId)
    if (!product) {
      throw new Error('Product not found')
    }
    await assertProductsInStockOrThrow([{ productId: product.id, name: product.name }])
    const siteUrl = (process.env.SITE_URL ?? 'http://localhost:3000').replace(/\/$/, '')
    const email = data.email?.trim()
      ? parseBuyerEmailFromCheckoutPayload(data.email)
      : defaultGuestEmail()
    if (!email) {
      throw new Error(
        'Buyer email is required before starting checkout.',
      )
    }
    const priceCents = Math.max(0, Math.round(product.price * 100))
    const successUrl = `${siteUrl}/checkout/success`
    const cancelUrl = `${siteUrl}/checkout/cancel`

    const sessionId = await createMoneyMotionCheckoutSession({
      apiKey,
      description: product.name,
      urls: {
        success: successUrl,
        cancel: cancelUrl,
        failure: cancelUrl,
      },
      customerEmail: email,
      lineItems: [
        {
          name: product.name,
          description: product.shortDescription?.slice(0, 500) || product.name,
          pricePerItemInCents: priceCents,
          quantity: 1,
        },
      ],
      metadata: {
        store: 'blkvault',
        productId: String(product.id),
        productIds: String(product.id),
        productName: product.name,
        buyerEmail: email,
      },
      currency: 'USD',
    })
    try {
      await ingestMoneyMotionPendingCheckout({
        checkoutSessionId: sessionId,
        customerEmail: email,
        productId: product.id,
        productName: product.name,
        amountUsd: product.price,
      })
    } catch (err) {
      console.error('[moneymotion] pending order ingest failed', err)
    }
    return {
      checkoutUrl: moneyMotionCheckoutUrl(sessionId),
      checkoutSessionId: sessionId,
    }
  })

const cartLineSchema = z.object({
  productId: z.number().int().positive(),
  name: z.string().min(1),
  unitPrice: z.number().nonnegative(),
  quantity: z.number().int().positive().max(100),
  smmPackageQuantity: z.number().int().positive().optional(),
  smmTiktokTarget: z.string().trim().optional(),
  smmServiceId: z.number().int().positive().optional(),
  smmTierId: z.string().trim().optional(),
  robuxAmount: z.number().int().positive().optional(),
  robuxGamepassPrice: z.number().int().positive().optional(),
  robuxGamepassLink: z.string().trim().min(12).max(512).optional(),
  serverBoostCount: z.number().int().positive().optional(),
  serverBoostInviteLink: z.string().trim().min(12).max(512).optional(),
})

const cartCheckoutInputSchema = z.object({
  items: z.array(cartLineSchema).min(1).max(50),
  email: z.string().optional(),
})

export const createCartCheckoutSession = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => cartCheckoutInputSchema.parse(d))
  .handler(async ({ data }) => {
    const apiKey = getApiKey()
    if (!apiKey) throw new Error('MoneyMotion is not configured (set MONEYMOTION_API_KEY).')
    const email = data.email?.trim()
      ? parseBuyerEmailFromCheckoutPayload(data.email)
      : defaultGuestEmail()
    if (!email) throw new Error('Buyer email is required before starting checkout.')
    const siteUrl = (process.env.SITE_URL ?? 'http://localhost:3000').replace(/\/$/, '')
    await assertProductsInStockOrThrow(
      data.items.map((item) => ({ productId: item.productId, name: item.name })),
    )

    const { assertSoloCustomCheckoutLine } = await import('#bv/lib/customQuantityCheckout')
    assertSoloCustomCheckoutLine(data.items, 'Robux, Server Boost, and growth packages')

    const {
      validateServerBoostCartLine,
      serverBoostMetadataForCheckout,
      quoteServerBoostPurchase,
      isServerBoostProduct,
    } = await import('#bv/lib/serverBoostProduct')

    const robuxLines = data.items.filter((i) => i.robuxAmount != null)
    if (robuxLines.length > 0) {
      throw new Error('Robux orders can only be paid through PayPal F&F.')
    }

    const serverBoostLines = data.items.filter((i) => i.serverBoostCount != null)
    if (serverBoostLines.length > 0) {
      const line = serverBoostLines[0]!
      const product = await getMergedProductById(line.productId)
      const boostErr = validateServerBoostCartLine(line, product)
      if (boostErr) throw new Error(boostErr)
    }

    const smmLines = data.items.filter((i) => i.smmPackageQuantity != null)
    if (smmLines.length > 0) {
      const line = smmLines[0]!
      const product = await getMergedProductById(line.productId)
      if (!product || !isSmmProduct(product)) {
        throw new Error('This growth product is not available.')
      }
      const target = smmTargetFromCartLine(line)
      const targetErr = smmTargetValidationMessageForProduct(target, product.smmConfig)
      if (targetErr) throw new Error(targetErr)
      const { findSmmPackage, defaultSmmTierId } = await import('#bv/lib/smmTiers')
      const tierId = line.smmTierId?.trim() || defaultSmmTierId(product.smmConfig)
      const pkg = findSmmPackage(product.smmConfig, tierId, line.smmPackageQuantity!)
      if (!pkg || Math.abs(pkg.price - line.unitPrice) > 0.02) {
        throw new Error('Invalid package selection. Refresh the product page and try again.')
      }
    }

    const successUrl = `${siteUrl}/checkout/success`
    const cancelUrl = `${siteUrl}/checkout/cancel`

    const lineItems = data.items.map((line) => ({
      name: line.name,
      description: `Product #${line.productId}`,
      pricePerItemInCents: Math.max(0, Math.round(line.unitPrice * 100)),
      quantity: line.quantity,
    }))
    const total = data.items.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0)

    const first = data.items[0]
    let smmMeta: Record<string, string> = {}
    let serverBoostMeta: Record<string, string> = {}
    if (serverBoostLines.length === 1) {
      const line = serverBoostLines[0]!
      const product = await getMergedProductById(line.productId)
      if (product && isServerBoostProduct(product)) {
        const quote = quoteServerBoostPurchase(line.serverBoostCount!, product.serverBoostConfig)
        serverBoostMeta = serverBoostMetadataForCheckout(quote, line.serverBoostInviteLink!, product.serverBoostConfig)
      }
    }
    if (smmLines.length === 1) {
      const line = smmLines[0]!
      const product = await getMergedProductById(line.productId)
      if (product && isSmmProduct(product)) {
        const normalized =
          normalizeSmmTargetForProduct(smmTargetFromCartLine(line), product.smmConfig) ?? smmTargetFromCartLine(line)
        smmMeta = smmMetadataForCheckout({
          productId: product.id,
          packageQuantity: line.smmPackageQuantity!,
          tiktokTarget: normalized,
          serviceId: product.smmConfig.serviceId,
          tierId: line.smmTierId?.trim() || undefined,
        })
      }
    }

    const sessionId = await createMoneyMotionCheckoutSession({
      apiKey,
      description: `PulseDigital Cart (${data.items.length} item${data.items.length === 1 ? '' : 's'})`,
      urls: { success: successUrl, cancel: cancelUrl, failure: cancelUrl },
      customerEmail: email,
      lineItems,
      metadata: {
        store: 'blkvault',
        cartSize: String(data.items.length),
        cartTotalUsd: total.toFixed(2),
        buyerEmail: email,
        productId: String(first.productId),
        productIds: data.items.map((i) => String(i.productId)).join(','),
        productName: first.name,
        ...serverBoostMeta,
        ...smmMeta,
        ...(data.items.length > 1
          ? {
              cartSummary: data.items
                .map((i) => `${i.quantity}× ${i.name}`)
                .join('; ')
                .slice(0, 450),
            }
          : {}),
      },
      currency: 'USD',
    })
    const productName =
      data.items.length > 1
        ? data.items.map((i) => `${i.quantity}× ${i.name}`).join(' · ').slice(0, 200)
        : first.name
    try {
      const robuxAmount = robuxLines.length === 1 ? robuxLines[0]?.robuxAmount : undefined
      const robuxGamepassPrice = robuxLines.length === 1 ? robuxLines[0]?.robuxGamepassPrice : undefined
      const robuxGamepassLink = robuxLines.length === 1 ? robuxLines[0]?.robuxGamepassLink : undefined
      const serverBoostCount = serverBoostLines.length === 1 ? serverBoostLines[0]?.serverBoostCount : undefined
      const serverBoostInviteLink =
        serverBoostLines.length === 1 ? serverBoostLines[0]?.serverBoostInviteLink : undefined
      const smmLine = smmLines.length === 1 ? smmLines[0] : undefined
      const smmTiktokTarget = smmLine?.smmTiktokTarget?.trim() || undefined
      await ingestMoneyMotionPendingCheckout({
        checkoutSessionId: sessionId,
        customerEmail: email,
        productId: first.productId,
        productName,
        amountUsd: total,
        ...(robuxAmount != null ? { robuxAmount } : {}),
        ...(robuxGamepassPrice != null ? { robuxGamepassPrice } : {}),
        ...(robuxGamepassLink ? { robuxGamepassLink } : {}),
        ...(serverBoostCount != null ? { serverBoostCount } : {}),
        ...(serverBoostInviteLink ? { serverBoostInviteLink } : {}),
        ...(smmTiktokTarget ? { smmTiktokTarget } : {}),
      })
    } catch (err) {
      console.error('[moneymotion] pending cart order ingest failed', err)
    }
    return {
      checkoutUrl: moneyMotionCheckoutUrl(sessionId),
      checkoutSessionId: sessionId,
    }
  })

const donationCheckoutInputSchema = z.object({
  amountUsd: z.number().finite().positive().max(5000),
  email: z.string().optional(),
  note: z.string().trim().max(400).optional(),
})

/** Create a MoneyMotion checkout session for a donation (no catalog items). */
export const createDonationCheckoutSession = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => donationCheckoutInputSchema.parse(d))
  .handler(async ({ data }) => {
    const apiKey = getApiKey()
    if (!apiKey) throw new Error('MoneyMotion is not configured (set MONEYMOTION_API_KEY).')

    const email = data.email?.trim()
      ? parseBuyerEmailFromCheckoutPayload(data.email)
      : defaultGuestEmail()
    if (!email) throw new Error('Buyer email is required before starting checkout.')

    const siteUrl = (process.env.SITE_URL ?? 'http://localhost:3000').replace(/\/$/, '')
    const successUrl = `${siteUrl}/donation/thanks`
    const cancelUrl = `${siteUrl}/donation`

    const amountUsd = Math.round(data.amountUsd * 100) / 100
    const priceCents = Math.max(1, Math.round(amountUsd * 100))
    const note = data.note?.trim() || ''
    const description = note ? `Donation — ${note}`.slice(0, 180) : 'Donation — Support PulseDigital'

    const sessionId = await createMoneyMotionCheckoutSession({
      apiKey,
      description: 'PulseDigital Donation',
      urls: { success: successUrl, cancel: cancelUrl, failure: cancelUrl },
      customerEmail: email,
      lineItems: [
        {
          name: 'Donation',
          description,
          pricePerItemInCents: priceCents,
          quantity: 1,
        },
      ],
      metadata: {
        store: 'blkvault',
        kind: 'donation',
        donationUsd: amountUsd.toFixed(2),
        buyerEmail: email,
        note: note.slice(0, 380),
        productId: '999999',
        productIds: '999999',
        productName: 'Donation',
      },
      currency: 'USD',
    })

    try {
      await ingestMoneyMotionPendingCheckout({
        checkoutSessionId: sessionId,
        customerEmail: email,
        productId: 999999,
        productName: 'Donation',
        amountUsd,
      })
    } catch (err) {
      console.error('[moneymotion] pending donation ingest failed', err)
    }

    return {
      checkoutUrl: moneyMotionCheckoutUrl(sessionId),
      checkoutSessionId: sessionId,
    }
  })
